import { Tutor } from "@/types/tutor";

const CATEGORY_OPENINGS: Record<string, string[]> = {
  language: [
    "Start with one sentence you would actually say, and I will help you make it sound natural.",
    "Tell me the situation you want to handle, and we will turn it into a short speaking drill.",
    "Say anything you can in the target language; I will repair the highest-impact issue first.",
  ],
  academic: [
    "Give me the part that feels muddy, and I will turn it into one clear example and a check.",
    "Show me what you already understand, and I will find the missing step.",
    "Bring one problem or paragraph, and we will work it until you can explain it back.",
  ],
  coding: [
    "Paste the bug, concept, or feature you are stuck on, and we will debug the first blocker.",
    "Tell me what you are trying to build, and I will turn it into the next small coding step.",
    "Show me your current approach, and I will help you reason through it without just dumping code.",
  ],
  roleplay: [
    "Give me the real situation, and I will play the other side while coaching your next move.",
    "Pick the scene, and I will pressure-test your answer with quick feedback.",
    "Tell me who you need to speak to, and we will rehearse the first thirty seconds.",
  ],
  zen: [
    "Take one normal breath. What would help most right now: calm, focus, rest, or clarity?",
    "Arrive as you are. Tell me what your system needs, and I will guide a short practice.",
    "Start with one word for how you feel, and I will shape a calm session around it.",
  ],
  wellness: [
    "Tell me what feels loud right now, and we will slow it down into one manageable reflection.",
    "Start with the mood you are bringing in, and I will guide a calm, practical check-in.",
    "Name the thing you want to untangle, and we will work with it one question at a time.",
  ],
  general: [
    "Tell me the outcome you want, and I will turn it into a focused practice loop.",
    "Give me the messy version of the problem, and I will help you find the first useful move.",
    "Start with what you tried already, and I will help you get unstuck without a lecture.",
  ],
};

const KEYWORD_OPENINGS: Array<[RegExp, string]> = [
  [
    /\b(dutch|nederlands)\b/i,
    "Say one Dutch sentence you would use in real life, and I will make it sharper.",
  ],
  [
    /\b(spanish|espanol|español)\b/i,
    "Try one Spanish answer, even a rough one, and I will coach the next better version.",
  ],
  [
    /\b(french|français|francais)\b/i,
    "Give me one French phrase you need, and we will make it sound less textbook.",
  ],
  [
    /\b(interview|job)\b/i,
    "Give me the question you fear most, and we will build a concise answer under pressure.",
  ],
  [
    /\b(math|algebra|calculus|statistics)\b/i,
    "Show me the step where the math stops making sense, and we will rebuild from there.",
  ],
  [
    /\b(code|coding|javascript|python|react|debug)\b/i,
    "Show me the code or concept that is blocking you, and we will reason through the next fix.",
  ],
  [
    /\b(history|essay|writing)\b/i,
    "Bring the claim or paragraph you are working on, and we will make the argument cleaner.",
  ],
];

function stableIndex(value: string, length: number) {
  if (length <= 1) return 0;
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) | 0;
  }
  return Math.abs(hash) % length;
}

export function getTutorOpeningLine(tutor: Tutor) {
  const savedOpening =
    tutor.opening_message ||
    tutor.builder_spec?.sampleExchange?.find((entry) => entry.role === "tutor")?.text;
  if (savedOpening) return savedOpening;

  if (tutor.goal) {
    return `Let's work on ${tutor.goal}. I will start by finding your current level.`;
  }

  const searchable = [tutor.name, tutor.description, tutor.category, tutor.archetype]
    .filter(Boolean)
    .join(" ");
  const keywordOpening = KEYWORD_OPENINGS.find(([pattern]) => pattern.test(searchable))?.[1];
  if (keywordOpening) return keywordOpening;

  const categoryKey = (tutor.category || "general").toLowerCase();
  const options = CATEGORY_OPENINGS[categoryKey] || CATEGORY_OPENINGS.general;
  return options[
    stableIndex(`${tutor.id}:${tutor.name}:${tutor.description || ""}`, options.length)
  ];
}

export function getTutorFollowupLine(tutor: Tutor) {
  const categoryKey = (tutor.category || "general").toLowerCase();
  if (categoryKey === "language") {
    return "Good. Keep it short first; I will correct one thing and then you will try again.";
  }
  if (categoryKey === "coding") {
    return "Good. I will ask one narrowing question first so we fix the right layer.";
  }
  if (categoryKey === "roleplay") {
    return "Good. I will stay in character, then pause briefly for feedback after your attempt.";
  }
  if (categoryKey === "zen") {
    return "Good. I will keep this simple and safe: one breath, one body cue, then one gentle next step.";
  }
  return "Good. Give me one concrete example and I will help you repair the first useful step.";
}
